/**
 * 사업계획서 저장 ↔ 지출예산편성요구 이관
 *
 * 저장: IndexedDB bizPlanStore (구 mock localStorage 는 fallback)
 * 이관: 선택 예산차수(budgetKindCode)의 budgetExpenseRequest 에 master+detail insert
 */

import {
  defaultMasterRow,
  defaultDetailRow,
  loadExpenseRequests,
  saveExpenseRequests,
  enrichMasterRow,
} from 'budget/budgetExpenseRequestStore';
import { getCachedSubjectsByFilter } from 'budget/budgetStructureStore';
import { getCachedProjects } from 'budget/budgetProjectStore';
import { findRequestAdjStage } from 'budget/budgetRoundStore';
import { normalizeFieldValue } from 'budget/bizPlanHwpFieldApply';
import { resolveBizPlanForImport } from 'budget/bizPlanStore';

export const BIZ_PLAN_POC_STORE_PREFIX = 'bizPlanRhwpPoc';
export const BIZ_PLAN_IMPORT_REMARK_PREFIX = '사업계획서가져오기';

export function bizPlanPocStorageKey(tenantCode) {
  return `${BIZ_PLAN_POC_STORE_PREFIX}|${tenantCode || 'UNKNOWN'}`;
}

/** mock 저장 페이로드 읽기 */
export function loadBizPlanMock(tenantCode) {
  try {
    const raw = localStorage.getItem(bizPlanPocStorageKey(tenantCode));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** mock 저장 (작성 화면에서 호출) */
export function saveBizPlanMock(tenantCode, payload) {
  const key = bizPlanPocStorageKey(tenantCode);
  const next = {
    ...payload,
    tenantCode: tenantCode || payload?.tenantCode || '',
    savedAt: payload?.savedAt || new Date().toISOString(),
  };
  localStorage.setItem(key, JSON.stringify(next));
  return { key, payload: next };
}

/** "1,234" / "1234천원" → number (천원 단위 가정) */
export function parseThousandAmt(raw) {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const s = String(raw)
    .replace(/[,\s]/g, '')
    .replace(/천원|원|원정/g, '')
    .replace(/[^\d.-]/g, '');
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function field(fields, ...keys) {
  for (const k of keys) {
    const v = normalizeFieldValue(fields?.[k]);
    if (v) return v;
  }
  return '';
}

function resolveSubject(tenantCode, fiscalYear, subjectNameHint) {
  const hint = String(subjectNameHint || '').trim();
  if (!hint) return null;
  const list =
    getCachedSubjectsByFilter({
      tenantCode,
      fiscalYear,
      inoutDiv: '지출',
    }) || [];
  const active = list.filter((r) => r.useYn !== 'N' && r.mod !== 'D');
  const exact = active.find((r) => String(r.subjectName || '').trim() === hint);
  if (exact) return exact;
  const includes = active.find((r) => String(r.subjectName || '').includes(hint));
  if (includes) return includes;
  const reverse = active.find((r) => hint.includes(String(r.subjectName || '').trim()));
  return reverse || null;
}

function resolveProject(tenantCode, fiscalYear, { projectCode, projectName } = {}) {
  const code = String(projectCode || '').trim();
  const name = String(projectName || '').trim();
  const loaded = getCachedProjects({ tenantCode, fiscalYear }) || [];
  const active = (Array.isArray(loaded) ? loaded : []).filter((r) => r.useYn !== 'N');
  if (code) {
    const byCode = active.find((r) => String(r.projectCode || '').trim() === code);
    if (byCode) return byCode;
  }
  if (name) {
    const exact = active.find((r) => String(r.projectName || '').trim() === name);
    if (exact) return exact;
    const includes = active.find((r) => String(r.projectName || '').includes(name));
    if (includes) return includes;
  }
  return null;
}

/**
 * mock/store 페이로드 → 편성요구 master 1건(+ detail)
 */
export function buildExpenseRowFromBizPlan(mock, { tenantCode, fiscalYear } = {}) {
  const fields = mock?.fields || {};
  const picked = mock?.picked || {};

  const deptCode = String(picked.deptCode || '').trim();
  const deptName = String(
    picked.deptName || field(fields, 'header.deptName') || '',
  ).trim();

  let projectCode = String(picked.projectCode || '').trim();
  let projectName = String(
    picked.projectName || field(fields, 'doc.title') || '',
  ).trim();

  const project = resolveProject(tenantCode, fiscalYear, {
    projectCode,
    projectName,
  });
  if (project) {
    projectCode = project.projectCode || projectCode;
    projectName = project.projectName || projectName;
  }

  const subjectHint = field(fields, 'calc.line1.subjectName');
  const subject = resolveSubject(tenantCode, fiscalYear, subjectHint);

  const lineAmt = parseThousandAmt(fields['calc.line1.planAmt']);
  const totalAmt = parseThousandAmt(fields['calc.total.planAmt']);
  const costTotal = parseThousandAmt(fields['overview.costTotal']);
  const amt = lineAmt || totalAmt || costTotal || 0;

  const calcNarrative = field(fields, 'calcBasis.narrative');
  const calcDetail = field(fields, 'calc.line1.calcDetail');
  const contents = field(fields, 'overview.contents');

  // 산출내역은 항상 1건 생성 (금액 0이어도 가져오기 결과 확인 가능)
  const details = [
    defaultDetailRow({
      calcLevel: 1,
      calcDesc:
        calcDetail ||
        calcNarrative ||
        contents ||
        projectName ||
        '사업계획서 이관',
      calcBasis: calcNarrative || contents || '',
      calcAmt: amt,
      remark: '',
      mod: 'A',
    }),
  ];

  const remarkParts = [
    `${BIZ_PLAN_IMPORT_REMARK_PREFIX}|${mock?.savedAt || mock?.updatedAt || mock?.planId || ''}`,
    mock?.formId ? `form=${mock.formId}` : '',
    mock?.planId ? `plan=${mock.planId}` : '',
    subjectHint && !subject ? `과목미매칭:${subjectHint}` : '',
    !projectCode && projectName ? `사업코드미매칭:${projectName}` : '',
  ].filter(Boolean);

  return enrichMasterRow(
    defaultMasterRow({
      deptCode,
      deptName,
      projectCode,
      projectName,
      subjectUid: subject?.subjectUid || '',
      subjectCode: subject?.subjectCode || '',
      subjectDispCode: subject?.subjectDispCode || subject?.subjectCode || '',
      subjectName: subject?.subjectName || subjectHint || '',
      budgetType: subject?.budgetType || '',
      remark: remarkParts.join(' · '),
      details,
      mod: 'A',
      sourceBizPlanSavedAt: mock?.savedAt || mock?.updatedAt || '',
      sourceBizPlanId: mock?.planId || '',
    }),
  );
}

/**
 * 선택 예산차수에 사업계획서(store 최신 또는 mock) → 지출편성요구 insert
 */
export async function importBizPlanToExpenseRequest({
  tenantCode,
  fiscalYear,
  round,
  planId,
  replaceSameSource = false,
} = {}) {
  if (!round) {
    return { ok: false, reason: '예산차수를 선택하세요.' };
  }
  if (round.budgetConfirmYn === 'Y') {
    return { ok: false, reason: '예산이 확정된 차수에는 가져올 수 없습니다.' };
  }
  const budgetKindCode = String(round.budgetKindCode || '').trim();
  if (!budgetKindCode) {
    return { ok: false, reason: '선택한 차수에 결합코드(budgetKindCode)가 없습니다.' };
  }

  const reqStage = findRequestAdjStage(round.adjStages);
  if (!reqStage) {
    return {
      ok: false,
      reason: '요구/조정차수가 없습니다. 「부서요구」 단계를 먼저 추가하세요.',
    };
  }
  if (reqStage.adjConfirmYn === 'Y') {
    return {
      ok: false,
      reason: `요구단계(${reqStage.adjName || ''})가 확정되어 가져올 수 없습니다.`,
    };
  }

  const stored = await resolveBizPlanForImport({
    tenantCode,
    fiscalYear: String(fiscalYear || round.fiscalYear || ''),
    planId,
  });
  const mock = stored || loadBizPlanMock(tenantCode);
  if (!mock) {
    return {
      ok: false,
      reason: '저장된 사업계획서가 없습니다. 사업계획서 작성에서 「저장」을 먼저 하세요.',
    };
  }

  const picked = mock.picked || {};
  const fields = mock.fields || {};
  const hasDept = !!(picked.deptCode || picked.deptName || field(fields, 'header.deptName'));
  const hasProject = !!(picked.projectCode || picked.projectName || field(fields, 'doc.title'));
  if (!hasDept && !hasProject) {
    return {
      ok: false,
      reason:
        '사업계획서에 부서·사업 정보가 없습니다. 작성 화면에서 「부서」「사업」선택·편성연동 입력 후 다시 저장하세요.',
    };
  }

  const dims = {
    tenantCode,
    fiscalYear: String(fiscalYear || round.fiscalYear || ''),
    budgetKindCode,
    acctClass: '',
  };

  let existing = await loadExpenseRequests(dims);
  if (replaceSameSource && (mock.savedAt || mock.planId)) {
    const tag = mock.planId
      ? `plan=${mock.planId}`
      : `${BIZ_PLAN_IMPORT_REMARK_PREFIX}|${mock.savedAt}`;
    existing = existing.filter((r) => !String(r.remark || '').includes(tag));
  }

  const row = buildExpenseRowFromBizPlan(mock, {
    tenantCode,
    fiscalYear: dims.fiscalYear,
  });

  const dup = existing.some(
    (r) =>
      String(r.deptCode || '') === String(row.deptCode || '') &&
      String(r.projectCode || '') === String(row.projectCode || '') &&
      String(r.subjectCode || '') === String(row.subjectCode || '') &&
      r.mod !== 'D',
  );

  const nextRows = [...existing.map((r) => ({ ...r, mod: r.mod === 'D' ? 'D' : '' })), row];
  const saved = await saveExpenseRequests(dims, nextRows);

  const filledKeys = Object.keys(fields).filter((k) => normalizeFieldValue(fields[k]));

  return {
    ok: true,
    inserted: 1,
    duplicateHint: dup,
    budgetKindCode,
    roundName: round.roundName || '',
    reqStageName: reqStage.adjName || '',
    row,
    deptSync: saved?.deptSync,
    mockSavedAt: mock.savedAt || mock.updatedAt,
    planId: mock.planId || '',
    fieldsExtracted: filledKeys.length > 0,
    filledFieldCount: filledKeys.length,
  };
}
